import { faFileCertificate, faPen } from "@fortawesome/pro-regular-svg-icons";
import { faEllipsisH } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Divider, Segment } from "semantic-ui-react";
import AccessibleCheckbox from "../../../../../../../../components/global_components/AccessibleCheckbox";
import { useClient } from "../../../../../../../../hooks/use-client";
import { GetNotifyNotificationContentSearchResponse } from "../../../../../../../../queries/notify";
import useSite from "../../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { NotifyNotificationsDates } from "../components/NotifyNotificationsDates";
import {
  NotifyNotificationsMetaStat,
  NotifyNotificationsMetaStatType,
} from "../components/NotifyNotificationsMetaStat";
import { NotifyNotificationsStatusLabel } from "../components/NotifyNotificationsStatusLabel";
import { ChannelIconList } from "./NotifyNotificationsChannelIcon";

export function NotifyNotificationsCard({
  content,
  showCheckbox = false,
}: {
  content: GetNotifyNotificationContentSearchResponse["data"][number];
  showCheckbox?: boolean;
}) {
  const site = useSite();
  const { name: siteName } = useClient(site);

  const [isSelected, setIsSelected] = useState(false);

  const shouldShowStats = content.status === "Active" || content.status === "Archived";

  return (
    <Link
      to={`/${site}/notify/notifications/${content.id}`}
      onClick={(e) => {
        if (showCheckbox) {
          e.stopPropagation();
          e.preventDefault();
          setIsSelected((prev) => !prev);
        }
      }}
    >
      <Segment className='tw-group tw-flex tw-h-[210px] tw-w-full tw-flex-col tw-justify-between tw-overflow-hidden tw-p-5 tw-text-text hover:tw-shadow-md'>
        <div
          className={classNames("tw-absolute tw-left-3 tw-top-3 tw-cursor-pointer group-hover:tw-block", {
            "tw-hidden": !isSelected && !showCheckbox,
          })}
        >
          <AccessibleCheckbox checked={isSelected} onChange={() => setIsSelected((prev) => !prev)} />
        </div>
        <div className='tw-flex tw-h-full tw-gap-3'>
          {
            content.mainImageUrl && (
              <img
                src={content.mainImageUrl}
                alt='first in document'
                className='tw-aspect-square tw-h-[126px] tw-rounded-[4px] tw-object-cover'
              />
            ) /* TODO: also parse out the alt text */
          }
          <div className='tw-flex tw-w-full tw-flex-col tw-gap-1'>
            <div className='tw-flex tw-items-center tw-justify-between'>
              <h4 className='tw-m-0 tw-text-base tw-font-semibold tw-text-text-hint'>
                From {content.departmentSentFrom?.name ?? `${siteName}`}
              </h4>
              <div className='tw-flex tw-gap-2'>
                {content.status === "Pending" && (
                  <Button primary className='tw-m-0 tw-flex tw-gap-2'>
                    <FontAwesomeIcon icon={faFileCertificate} />
                    Review
                  </Button>
                )}
                {content.status === "Draft" && (
                  <Button primary className='tw-m-0 tw-flex tw-gap-2'>
                    <FontAwesomeIcon icon={faPen} />
                    Edit
                  </Button>
                )}
                <Button className='button-ghost black icon tw-m-0'>
                  <FontAwesomeIcon icon={faEllipsisH} />
                </Button>
              </div>
            </div>
            <div>
              <h2 className='tw-m-0 tw-line-clamp-1 tw-text-xl'>{content.headline}</h2>
              <h3 className='tw-m-0 tw-line-clamp-1 tw-text-lg'>{content.tagline}</h3>
            </div>
            {shouldShowStats && (
              <div className='tw-mt-auto tw-flex tw-gap-4'>
                <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.NotificationsDelivered} value={0} />
                <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.NotificationsFailed} value={0} />
                <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.Subscribers} value={0} />
                <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.Views} value={0} />
                <NotifyNotificationsMetaStat type={NotifyNotificationsMetaStatType.Likes} value={0} />
              </div>
            )}
          </div>
        </div>
        <Divider className='tw-my-3' />
        <div className='tw-flex tw-gap-2'>
          <NotifyNotificationsStatusLabel type={content.status} />
          <NotifyNotificationsDates content={content} />
          <div className='tw-ml-auto'>
            <ChannelIconList channels={content.channels} />
          </div>
        </div>
      </Segment>
    </Link>
  );
}
